import { useMutation } from 'react-query';
import {
  CancelInvoice,
  ConsultInvoice,
  InvoicePdf,
  InvoiceXml,
  MirrorInvoicePdf,
  MirrorInvoiceXml,
  TransmitInvoice,
  invoiceMail,
} from './Invoice.services';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import invoiceHandleError from '../invoice.error.transmit';
import {
  CancelInvoiceRequest,
  InvoiceConsultRequest,
  InvoiceMailRequest,
  InvoiceMirrorRequest,
  InvoiceTransmitRequest,
  invoiceTypeNotification,
} from './Invoice.types';

const INVOICE_LIST_QUERY = 'invoices';
const INVOICE_DETAIL_QUERY = 'invoice';

export function useCancelInvoiceHub() {
  const queryChanel = useQueryChanel();

  return useMutation(
    (invoice: CancelInvoiceRequest) => CancelInvoice(invoice),
    {
      async onSuccess(_, { invoiceId, type }) {
        queryChanel({
          invalidateQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
          ],
          refetchQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
          ],
        });

        notification.success({
          message: 'Sucesso',
          description: `${invoiceTypeNotification[type]} cancelada com sucesso`,
        });
      },

      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useConsultInvoiceHub() {
  const queryChanel = useQueryChanel();

  return useMutation(
    (invoice: InvoiceConsultRequest) => ConsultInvoice(invoice),
    {
      async onSuccess(message, { invoiceId }) {
        if (message) notification.success({ message });

        queryChanel({
          invalidateQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
            ['invoiceDetail', invoiceId],
          ],
          refetchQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
            ['invoiceDetail', invoiceId],
          ],
        });
      },

      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useTransmitInvoiceHub() {
  const queryChanel = useQueryChanel();

  return useMutation(
    (invoice: InvoiceTransmitRequest) => TransmitInvoice(invoice),
    {
      async onSuccess(_, { invoiceId, type }) {
        queryChanel({
          invalidateQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
            ['invoiceDetail', invoiceId],
          ],
          refetchQueries: [
            [INVOICE_DETAIL_QUERY, invoiceId],
            [INVOICE_LIST_QUERY],
            ['invoiceDetail', invoiceId],
          ],
        });

        notification.success({
          message: 'Sucesso',
          description: `${invoiceTypeNotification[type]} transmitida com sucesso`,
        });
      },
      onError(_, { invoiceId }) {
        queryChanel({
          refetchQueries: [[INVOICE_DETAIL_QUERY, invoiceId]],
        });
      },
    },
  );
}

export function useMirrorInvoiceHubPdf() {
  return useMutation(
    (invoice: InvoiceMirrorRequest) => MirrorInvoicePdf(invoice),
    {
      onSuccess() {
        notification.success({
          message: 'Sucesso',
          description:
            'Pré-visualização do PDF da Nota Fiscal gerada com sucesso!',
        });
      },

      onError(err) {
        invoiceHandleError(err);
      },
    },
  );
}

export function useMirrorInvoiceHubXml() {
  return useMutation(MirrorInvoiceXml, {
    onSuccess() {
      notification.success({
        message: 'Sucesso',
        description:
          'Pré-visualização do XML da Nota Fiscal gerada com sucesso!',
      });
    },

    onError(err) {
      invoiceHandleError(err);
    },
  });
}

export function useInvoiceHubMail() {
  return useMutation((invoice: InvoiceMailRequest) => invoiceMail(invoice), {
    async onSuccess(_, { type }) {
      notification.success({
        message: 'Sucesso',
        description: `${invoiceTypeNotification[type]} enviada para o e-mail do destinatário.`,
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceHubPdf() {
  return useMutation(InvoicePdf, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'PDF da Nota Fiscal Gerado com Sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceHubXml() {
  return useMutation(InvoiceXml, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'XML da Nota Fiscal gerado com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}
